import { Box, Typography } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { type FC, useMemo, useState } from "react";
import InfiniteScroll from "../../components/infiniteScroll/InfiniteScroll";
import { useLimit } from "../../misc/useLimit";
import { theme2 } from "../../theme";
import ClientSelectionToolbar from "./ClientSelectionToolbar";
import ClientHelpdeskDialog from "./dialogs/ClientHelpdeskDialog";
import ClientNotesDialog from "./dialogs/ClientNotesDialog";
import ClientStatsDialog from "./dialogs/ClientStatsDialog";
import MassMessageDialog from "./dialogs/MassMessageDialog";
import ActiveFilterChips from "./filters/ActiveFilterChips";
import ClientFiltersPanel from "./filters/ClientFiltersPanel";
import ClientsTable from "./table/ClientsTable";
import { useClientDialog } from "./useClientDialog";
import { useClientFilters } from "./useClientFilters";
import { useClientSelection } from "./useClientSelection";
import { useCustomerSummaries } from "./useCustomerSummaries";

const AdminClientsList: FC = () => {
  const selection = useClientSelection();
  const { limit, loadMore, resetLimit } = useLimit();
  const filters = useClientFilters({
    onChange: () => {
      selection.clear();
      resetLimit();
    },
  });
  const { clientFor, openDialog, closeDialog } = useClientDialog();
  const [massMessageOpen, setMassMessageOpen] = useState(false);

  const { summaries: rows, isFetching } = useCustomerSummaries(filters.appliedFilters);
  const rowIds = useMemo(() => rows.map((row) => row.id), [rows]);
  const visibleRows = useMemo(() => rows.slice(0, limit), [rows, limit]);
  const allSelected = selection.areAllSelected(rowIds);
  const selectedClients = useMemo(
    () => rows.filter((client) => selection.selectedIds.has(client.id)),
    [rows, selection.selectedIds],
  );

  return (
    <ThemeProvider theme={theme2}>
      <Box sx={{ animation: "fadeIn 0.4s ease-out", pb: 4, mt: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2 }}>
          Lista klientów
        </Typography>
        <ClientFiltersPanel
          appliedFilters={filters.appliedFilters}
          onApply={filters.applyFilters}
          onReset={filters.resetFilters}
        />
        <ActiveFilterChips
          filters={filters.appliedFilters}
          resultCount={rows.length}
          onRemove={filters.removeFilter}
          onReset={filters.resetFilters}
        />
        <ClientSelectionToolbar
          selectedCount={selection.selectedIds.size}
          allSelected={allSelected}
          selectable={rows.length > 0}
          onToggleAll={() => selection.toggleAll(rowIds)}
          onClear={selection.clear}
          onOpenMassMessage={() => setMassMessageOpen(true)}
        />
        <InfiniteScroll dataLength={visibleRows.length} fetchData={loadMore} hasMore={limit < rows.length}>
          <ClientsTable
            rows={visibleRows}
            loading={isFetching}
            allSelected={allSelected}
            someSelected={selection.isAnySelected(rowIds)}
            isSelected={selection.isSelected}
            onToggleRow={selection.toggle}
            onToggleAll={() => selection.toggleAll(rowIds)}
            onOpenDialog={openDialog}
          />
        </InfiniteScroll>
        <ClientNotesDialog client={clientFor("notes")} onClose={closeDialog} />
        <ClientHelpdeskDialog client={clientFor("helpdesk")} onClose={closeDialog} />
        <ClientStatsDialog client={clientFor("stats")} onClose={closeDialog} />
        <MassMessageDialog
          open={massMessageOpen}
          recipients={selectedClients}
          onClose={() => setMassMessageOpen(false)}
        />
      </Box>
    </ThemeProvider>
  );
};

export default AdminClientsList;
